import React from 'react';
import { Tag as AntTag, TagProps as AntTagProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export type TagVariant = 'primary' | 'secondary';
export type TagColor =
  // Semantic names
  | 'default'
  | 'success'
  | 'warning'
  | 'error'
  // Ant Design preset colors
  | 'blue'
  | 'cyan'
  | 'green'
  | 'red'
  | 'purple'
  | 'pink'
  | 'magenta'
  | 'yellow'
  | 'gold'
  | 'tan'
  | 'orange'
  | 'volcano'
  | 'lime'
  | 'geekblue'
  | 'processing';

export interface TagProps extends Omit<AntTagProps, 'color'> {
  variant?: TagVariant;
  color?: TagColor;
  className?: string;
  disabled?: boolean;
}

const useStyles = createUseStyles((theme: ThemeType) => {
  const baseStyles = {
    borderWidth: theme.border.width.sm,
    borderStyle: 'solid',
    padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
    borderRadius: theme.radius.md,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.regular,
  };

  return {
  // Primary variant - Semantic colors
  primaryDefault: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.brand.default.subtle,
    color: theme.color.semantic.brand.default.primary,
    borderColor: theme.color.semantic.brand.default.primary,
  },
  primarySuccess: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.green.subtle,
    color: theme.color.semantic.accent.green.strong,
    borderColor: theme.color.semantic.accent.green.strong,
  },
  primaryWarning: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.orange.subtle,
    color: theme.color.semantic.accent.orange.strong,
    borderColor: theme.color.semantic.accent.orange.strong,
  },
  primaryError: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.brand.danger.inverse,
    color: theme.color.semantic.brand.danger.primary,
    borderColor: theme.color.semantic.brand.danger.light,
  },

  // Primary variant - Preset colors
  primaryBlue: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.blue.subtle,
    color: theme.color.semantic.accent.blue.strong,
    borderColor: theme.color.semantic.accent.blue.strong,
  },
  primaryCyan: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.cyan.subtle,
    color: theme.color.semantic.accent.cyan.strong,
    borderColor: theme.color.semantic.accent.cyan.strong,
  },
  primaryGreen: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.green.subtle,
    color: theme.color.semantic.accent.green.strong,
    borderColor: theme.color.semantic.accent.green.strong,
  },
  primaryRed: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.red.subtle,
    color: theme.color.semantic.accent.red.strong,
    borderColor: theme.color.semantic.accent.red.strong,
  },
  primaryPurple: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.purple.subtle,
    color: theme.color.semantic.accent.purple.strong,
    borderColor: theme.color.semantic.accent.purple.strong,
  },
  primaryPink: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.pink.subtle,
    color: theme.color.semantic.accent.pink.strong,
    borderColor: theme.color.semantic.accent.pink.strong,
  },
  primaryMagenta: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.pink.subtle,
    color: theme.color.semantic.accent.pink.strong,
    borderColor: theme.color.semantic.accent.pink.strong,
  },
  primaryYellow: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.yellow.subtle,
    color: theme.color.semantic.accent.yellow.strong,
    borderColor: theme.color.semantic.accent.yellow.strong,
  },
  primaryGold: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.yellow.subtle,
    color: theme.color.semantic.accent.yellow.strong,
    borderColor: theme.color.semantic.accent.yellow.strong,
  },
  primaryTan: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.tan.subtle,
    color: theme.color.semantic.accent.tan.strong,
    borderColor: theme.color.semantic.accent.tan.strong,
  },
  primaryOrange: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.orange.subtle,
    color: theme.color.semantic.accent.orange.strong,
    borderColor: theme.color.semantic.accent.orange.strong,
  },
  primaryVolcano: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.orange.subtle,
    color: theme.color.semantic.accent.red.strong,
    borderColor: theme.color.semantic.accent.red.strong,
  },
  primaryLime: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.green.subtle,
    color: theme.color.semantic.accent.green.bold,
    borderColor: theme.color.semantic.accent.green.bold,
  },
  primaryGeekblue: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.purple.subtle,
    color: theme.color.semantic.accent.purple.strong,
    borderColor: theme.color.semantic.accent.purple.strong,
  },
  primaryProcessing: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.blue.subtle,
    color: theme.color.semantic.accent.blue.strong,
    borderColor: theme.color.semantic.accent.blue.strong,
  },

  // Secondary variant - Semantic colors
  secondaryDefault: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.background.neutral.subtle,
    color: theme.color.semantic.text.primary,
    borderColor: theme.color.semantic.border.primary,
  },
  secondarySuccess: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.green.subtle,
    color: theme.color.semantic.accent.green.strong,
    borderColor: theme.color.semantic.accent.green.strong,
  },
  secondaryWarning: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.orange.subtle,
    color: theme.color.semantic.accent.orange.strong,
    borderColor: theme.color.semantic.accent.orange.strong,
  },
  secondaryError: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.brand.danger.inverse,
    color: theme.color.semantic.brand.danger.primary,
    borderColor: theme.color.semantic.brand.danger.light,
  },

  // Secondary variant - Preset colors
  secondaryBlue: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.blue.subtle,
    color: theme.color.semantic.accent.blue.strong,
    borderColor: theme.color.semantic.accent.blue.strong,
  },
  secondaryCyan: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.cyan.subtle,
    color: theme.color.semantic.accent.cyan.strong,
    borderColor: theme.color.semantic.accent.cyan.strong,
  },
  secondaryGreen: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.green.subtle,
    color: theme.color.semantic.accent.green.strong,
    borderColor: theme.color.semantic.accent.green.strong,
  },
  secondaryRed: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.red.subtle,
    color: theme.color.semantic.accent.red.strong,
    borderColor: theme.color.semantic.accent.red.strong,
  },
  secondaryPurple: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.purple.subtle,
    color: theme.color.semantic.accent.purple.strong,
    borderColor: theme.color.semantic.accent.purple.strong,
  },
  secondaryPink: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.pink.subtle,
    color: theme.color.semantic.accent.pink.strong,
    borderColor: theme.color.semantic.accent.pink.strong,
  },
  secondaryMagenta: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.pink.subtle,
    color: theme.color.semantic.accent.pink.strong,
    borderColor: theme.color.semantic.accent.pink.strong,
  },
  secondaryYellow: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.yellow.subtle,
    color: theme.color.semantic.accent.yellow.strong,
    borderColor: theme.color.semantic.accent.yellow.strong,
  },
  secondaryGold: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.yellow.subtle,
    color: theme.color.semantic.accent.yellow.strong,
    borderColor: theme.color.semantic.accent.yellow.strong,
  },
  secondaryTan: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.tan.subtle,
    color: theme.color.semantic.accent.tan.strong,
    borderColor: theme.color.semantic.accent.tan.strong,
  },
  secondaryOrange: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.orange.subtle,
    color: theme.color.semantic.accent.orange.strong,
    borderColor: theme.color.semantic.accent.orange.strong,
  },
  secondaryVolcano: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.orange.subtle,
    color: theme.color.semantic.accent.red.strong,
    borderColor: theme.color.semantic.accent.red.strong,
  },
  secondaryLime: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.green.subtle,
    color: theme.color.semantic.accent.green.bold,
    borderColor: theme.color.semantic.accent.green.bold,
  },
  secondaryGeekblue: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.purple.subtle,
    color: theme.color.semantic.accent.purple.strong,
    borderColor: theme.color.semantic.accent.purple.strong,
  },
  secondaryProcessing: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.accent.blue.subtle,
    color: theme.color.semantic.accent.blue.strong,
    borderColor: theme.color.semantic.accent.blue.strong,
  },

  // Disabled state
  disabled: {
    ...baseStyles,
    backgroundColor: theme.color.semantic.background.neutral.soft,
    color: theme.color.semantic.text.soft,
    borderColor: theme.color.semantic.border.primary,
    opacity: theme.opacity[70],
  },
  };
});

const Tag: React.FC<TagProps> = ({
  variant = 'primary',
  color = 'default',
  className = '',
  disabled = false,
  children,
  ...rest
}) => {
  const classes = useStyles();

  const getTagClass = () => {
    if (disabled) {
      return classes.disabled;
    }

    const colorKey = color.charAt(0).toUpperCase() + color.slice(1);
    const classKey = `${variant}${colorKey}` as keyof typeof classes;
    return classes[classKey] || classes.primaryDefault;
  };

  return (
    <AntTag {...rest} className={`${getTagClass()} ${className}`.trim()}>
      {children}
    </AntTag>
  );
};

export default Tag;
